import React from 'react';
import { FileText, CheckCircle, Workflow, Key } from 'lucide-react';

const features = [
  {
    title: "Document Management",
    description: "Upload, manage, and download documents securely within your company's workspace",
    bullets: [
      "Multi-format support",
      "Company-isolated storage", 
      "Download anytime"
    ],
    icon: FileText,
  },
  {
    title: "Verification System",
    description: "Manual document review with approval, rejection, and audit tracking",
    bullets: [
      "Approve/reject with notes",
      "Timestamp tracking",
      "Export to Excel"
    ],
    icon: CheckCircle,
  },
  {
    title: "Structured Workflows",
    description: "Guided onboarding flows with role-based access and dashboards",
    bullets: [
      "Step-by-step onboarding",
      "Company & employee portals",
      "Progress visibility"
    ],
    icon: Workflow,
  },
  {
    title: "Company Code System",
    description: "Unique company codes link employees to organizations automatically",
    bullets: [
      "Easy employee registration",
      "Multi-company support",
      "Simple setup"
    ],
    icon: Key,
  }
];

const FeaturesSection: React.FC = () => {
  return (
    <div className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center">
          <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
            Features
          </h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Everything you need to streamline onboarding
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
            Powerful tools to manage employee document verification and onboarding
          </p>
        </div>

        <div className="mt-20">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => (
              <div key={feature.title} className="relative bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div>
                  <div className="absolute h-12 w-12 rounded-md bg-blue-500 flex items-center justify-center">
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-16">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      {feature.description}
                    </p>
                    <ul className="space-y-2">
                      {feature.bullets.map((bullet, i) => (
                        <li key={i} className="flex items-start text-sm text-gray-700">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 text-center">
          <a
            href="/features"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Learn More About Features
          </a>
        </div>
      </div>
    </div>
  );
};

export default FeaturesSection;